// Unified Books API wrappers (BE chunk 2.5, shipped 2026-06-06; chunk 2.6,
// 2026-06-07; chunk 3).
//
// One endpoint serves both LIBRARY (curated) and USER_UPLOAD (community)
// books. Non-admin users see APPROVED books + their own (any reviewStatus);
// the server enforces visibility — the FE just reads what comes back.
//
// Spec: hs_backend_service_v3.0/specs/library_feature_frontend_handoff.md
// §6.0 + §6.2. FE plan: specs/library_feature_FE_plan_v2_060726.md.

import { apiClient } from '../apiClient'
import type { Book, BookReviewStatus, BookSource, ReadingGuide } from '@/types'

export interface FetchBooksParams {
  source?: BookSource
  featured?: boolean
  theme?: string
  ageMin?: number
  ageMax?: number
  // Restrict to books owned by the requesting user (USER_UPLOAD only,
  // since LIBRARY rows have ownerId=null).
  mine?: boolean
  // ADMIN-only; ignored server-side for non-admins. The hub doesn't use it
  // (admin tooling lives in a separate app) but the param is exposed so
  // consumers can specify it explicitly if needed.
  reviewStatus?: BookReviewStatus
  limit?: number
  offset?: number
}

interface ListResponse {
  books: Book[]
}

// The BE caps `limit` at 100 per request. The shelf filters/searches
// entirely client-side over the full set (counts must be exact), so we
// page through until a short page and return everything — durable to any
// catalog size, no silent truncation at 50/100. Callers that pass an
// explicit `limit` opt out (single page) — used by any bounded consumer.
const BOOKS_PAGE_SIZE = 100

export async function fetchBooks(params?: FetchBooksParams): Promise<Book[]> {
  // Explicit limit → single request, honored as-is.
  if (params?.limit !== undefined) {
    const { data } = await apiClient.get<ListResponse | Book[]>(
      '/api/content/books',
      { params },
    )
    return Array.isArray(data) ? data : data.books
  }

  const all: Book[] = []
  for (let offset = 0; ; offset += BOOKS_PAGE_SIZE) {
    const { data } = await apiClient.get<ListResponse | Book[]>(
      '/api/content/books',
      { params: { ...params, limit: BOOKS_PAGE_SIZE, offset } },
    )
    // Tolerate both wrapped (`{ books: [...] }` per spec) and bare-array
    // responses so a future BE refactor doesn't break the FE.
    const page = Array.isArray(data) ? data : data.books
    all.push(...page)
    if (page.length < BOOKS_PAGE_SIZE) break
  }
  return all
}

export async function fetchBookById(id: string): Promise<Book | null> {
  try {
    const { data } = await apiClient.get<Book>(`/api/content/books/${id}`)
    return data
  } catch (err) {
    // 404s (soft-deleted, or PENDING/REJECTED on a non-owner request, per
    // BE §7.6 — no existence leak) surface as null so consumers can render
    // an empty-state without try/catch.
    if (axiosNotFound(err)) return null
    throw err
  }
}

// Soft delete. BE authorizes by `book.source`:
//   - LIBRARY      → ADMIN only
//   - USER_UPLOAD  → owner only
// FE should hide the affordance when the current user isn't the owner; the
// server enforces the rule regardless.
export async function deleteBook(id: string): Promise<void> {
  await apiClient.delete(`/api/content/books/${id}`)
}

// ---------------------------------------------------------------------------
// Guide creation from a Book (BE chunk 3).
// ---------------------------------------------------------------------------
// `POST /api/content/guides { bookId, ... }` creates a new ReadingGuide row
// referencing the Book. BE validates that the book exists, isn't deleted,
// status=READY, and ownership rules — LIBRARY: any authed user; USER_UPLOAD:
// caller must be the book's owner (owner can also use own PENDING books).
// Returns the created ReadingGuide row with the `book` joined.

export interface CreateGuideFromBookInput {
  bookId: string
  title?: string                  // defaults to book.title server-side
  // Wire-optional (BE validates ownership only when present). The normal
  // flows always pass it; the admin test page creates learner-less guides.
  learnerId?: string
  learnerStageAtGen: number | null
  tenantId?: string
}

export async function createGuideFromBook(
  input: CreateGuideFromBookInput,
): Promise<ReadingGuide> {
  const { data } = await apiClient.post<ReadingGuide>(
    '/api/content/guides',
    { tenantId: 'default', ...input },
  )
  return data
}

// ---------------------------------------------------------------------------

function axiosNotFound(err: unknown): boolean {
  // apiClient flattens errors to Error & { status } (no axios `response`).
  return (err as { status?: number } | null)?.status === 404
}
